import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import consultantApi from '../../services/consultantApi';
import api from '../../services/api';
import { validateJobForm, cleanJobFormData, getJobErrorMessage } from '../../utils/jobValidation';
import './CreateJob.css';

const ConsultantCreateJob = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('self');
  const [showCompanyName, setShowCompanyName] = useState(false);

  // Master data
  const [jobTypes, setJobTypes] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [educations, setEducations] = useState([]);
  const [consultantSkills, setConsultantSkills] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [newSkill, setNewSkill] = useState('');
  const [customBenefitInput, setCustomBenefitInput] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    benefits: [],
    customBenefits: [], // For custom text benefits
    jobType: '',
    shiftType: '',
    educationRequired: '',
    applicationMethod: {
      type: 'internal'
    },
    workMode: 'onsite',
    location: {
      country: '',
      state: '',
      city: '',
      address: ''
    },
    salary: {
      min: 0,
      max: 0,
      currency: 'INR',
      period: 'monthly',
      negotiable: false
    },
    experienceRequired: {
      min: '',
      max: ''
    },
    requiredSkills: [],
    optionalSkills: [],
    numberOfPositions: 0,
    postUnderOwnName: true,
    showCompanyName: false,
    status: 'draft'
  });

  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      // Fetch master data (excluding benefits for now)
      // Backend returns: { success: true, data: [...] } via ApiResponse
      // Axios interceptor does response.data, so we get: { success: true, data: [...] }
      const [jobTypesRes, shiftTypesRes, educationsRes, consultantSkillsRes] = await Promise.all([
        api.get('/master/job-types'),
        api.get('/master/shift-types'),
        api.get('/master/educations'),
        api.get('/consultant/skills').catch(() => ({ data: [] })) // Get consultant skills from consultants endpoint
      ]);

      // ApiResponse format: { success: true, data: [...] }
      // So res.data is the array we want
      const extract = (res) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        return [];
      };

      setJobTypes(extract(jobTypesRes));
      setShiftTypes(extract(shiftTypesRes));
      setEducations(extract(educationsRes));
      setConsultantSkills(extract(consultantSkillsRes));

      // Benefits: Try multiple endpoints to get ALL benefits including admin-added ones
      let allBenefits = [];

      try {
        // First try: Get consultant-specific benefits (includes admin + consultant's own)
        const consultantBenefitsRes = await api.get('/consultant/benefits');
        const consultantBenefits = consultantBenefitsRes.data || [];
        allBenefits = [...consultantBenefits];
      } catch (consultantError) {
        // Consultant benefits endpoint failed
      }

      try {
        // Second try: Get master benefits (admin-created only)
        const masterBenefitsRes = await api.get('/master/benefits');
        const masterBenefits = masterBenefitsRes.data || [];

        // Merge and deduplicate by _id
        const mergedBenefits = [...allBenefits];
        masterBenefits.forEach(masterBenefit => {
          if (!mergedBenefits.find(b => b._id === masterBenefit._id)) {
            mergedBenefits.push(masterBenefit);
          }
        });
        allBenefits = mergedBenefits;
      } catch (masterError) {
        // Master benefits endpoint failed
      }

      // If we have benefits from API, use them
      if (allBenefits.length > 0) {
        setBenefits(allBenefits);
      }
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to load master data. Please refresh.');
    }

    // Fetch companies separately so one failure doesn't block master data
    try {
      const companiesRes = await consultantApi.getMyCompanies();
      // api interceptor already did response.data, so companiesRes = { success, data: { active:[..], pending:[..] } }
      const raw = companiesRes?.data?.data || companiesRes?.data || {};
      const assocList = Array.isArray(raw) ? raw : (raw?.active || []);
      const companyList = assocList
        .filter(assoc => {
          const status = assoc.status || assoc.associationStatus;
          return !status || status === 'accepted' || status === 'active';
        })
        .map(assoc => ({
          _id: assoc.company?._id || assoc.companyId?._id || assoc.companyId,
          name: assoc.company?.name || assoc.companyId?.name || 'Unknown Company',
          industry: assoc.company?.industry || assoc.companyId?.industry,
        }))
        .filter(c => c._id);
      setCompanies(companyList);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    const sanitized = value.replace(/\D/g, '');
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: sanitized === '' ? '' : Number(sanitized)
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: sanitized === '' ? '' : Number(sanitized)
      }));
    }
  };

  const handleSkillsChange = (e) => {
    const { value, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      requiredSkills: checked
        ? [...prev.requiredSkills, value]
        : prev.requiredSkills.filter(id => id !== value)
    }));
  };

  const handleBenefitsChange = (e) => {
    const { value, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      benefits: checked
        ? [...prev.benefits, value]
        : prev.benefits.filter(id => id !== value)
    }));
  };

  const handleAddCustomBenefit = () => {
    const trimmed = customBenefitInput.trim();
    if (trimmed && !formData.customBenefits.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        customBenefits: [...prev.customBenefits, trimmed]
      }));
      setCustomBenefitInput('');
    }
  };

  const handleRemoveCustomBenefit = (benefitToRemove) => {
    setFormData(prev => ({
      ...prev,
      customBenefits: prev.customBenefits.filter(b => b !== benefitToRemove)
    }));
  };

  const handleAddConsultantSkill = async () => {
    const trimmed = newSkill.trim();
    if (!trimmed) return;

    try {
      const res = await api.post('/consultant/skills', {
        name: trimmed
      });

      const createdSkill = res.data;

      // Add to consultant skills list
      setConsultantSkills(prev => [...prev, createdSkill]);

      // Auto select it as required skill
      setFormData(prev => ({
        ...prev,
        requiredSkills: [...prev.requiredSkills, createdSkill._id]
      }));

      setNewSkill('');
      toast.success('Skill added successfully!');
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.message || error?.message || 'Failed to add skill';
      toast.error(errorMessage);
    }
  };

  const handleRemoveConsultantSkill = async (skillId) => {
    try {
      await api.delete(`/consultant/skills/${skillId}`);
      setConsultantSkills(prev => prev.filter(s => s._id !== skillId));

      // Remove from form selection if present
      setFormData(prev => ({
        ...prev,
        requiredSkills: prev.requiredSkills.filter(id => id !== skillId),
        optionalSkills: prev.optionalSkills?.filter(id => id !== skillId) || []
      }));

      toast.success('Skill deleted successfully!');
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.message || error?.message || 'Failed to delete skill';
      toast.error(errorMessage);
    }
  };

  const handleSubmit = async (e, status = 'draft') => {
    e.preventDefault();

    if (formData.experienceRequired.min === '' || formData.experienceRequired.max === '') {
      toast.error('Min and Max Experience are required');
      return;
    }

    if (Number(formData.experienceRequired.max) < Number(formData.experienceRequired.min)) {
      toast.error('Max Experience should be greater than or equal to Min Experience');
      return;
    }

    const chosenCompany = selectedCompanyId !== 'self'
      ? companies.find(c => c._id === selectedCompanyId)
      : null;

    const jobData = {
      ...formData,
      status,
      salary: {
        ...formData.salary,
        min: parseInt(formData.salary.min) || 0,
        max: parseInt(formData.salary.max) || 0
      },
      experienceRequired: {
        min: parseInt(formData.experienceRequired.min) || 0,
        max: parseInt(formData.experienceRequired.max) || 0
      },
      numberOfPositions: parseInt(formData.numberOfPositions) || 1,
      companyId: chosenCompany?._id || null,
      postUnderOwnName: selectedCompanyId === 'self',
      postedByName: selectedCompanyId === 'self' ? user?.name : undefined,
      showCompanyName: selectedCompanyId !== 'self' ? showCompanyName : false,
    };

    // Validate form data
    const validation = validateJobForm(jobData);
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      toast.error(firstError);
      return;
    }

    try {
      setLoading(true);

      // Clean data before submission
      const cleanedData = cleanJobFormData(jobData);
      if (!cleanedData.companyId) delete cleanedData.companyId;

      const response = await consultantApi.createJob(cleanedData);
      const job = response.data;

      // Show appropriate message based on approval status
      if (job.approvalStatus === 'approved' && job.status === 'active') {
        toast.success('Job posted successfully! It\'s now live and visible to candidates.');
      } else {
        toast.success('Job created as draft! You can edit it or send for approval when ready.');
      }

      navigate('/consultant/jobs');
    } catch (error) {
      console.error('Error creating job:', error);
      const errorMessage = getJobErrorMessage(error.error || error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ccj-container">
      <div className="ccj-header">
        <div>
          <h1 className="ccj-title">Create Job Posting</h1>
          <p className="ccj-subtitle">Post a job on your own behalf or for an associated company</p>
        </div>
        <button onClick={() => navigate('/consultant/jobs')} className="ccj-btn-back">
          <span className="material-symbols-outlined">arrow_back</span>
          Back to Jobs
        </button>
      </div>

      <form className="ccj-form">
        {/* Post As Section */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">badge</span>
            Post As
          </h2>
          <div className="ccj-info-box">
            <span className="material-symbols-outlined">info</span>
            <p>Choose whether to post under <strong>your own name</strong> or on behalf of an <strong>accepted company</strong>. To add companies, go to the Companies page and send/accept association requests.</p>
          </div>
          <div className="ccj-field">
            <label className="ccj-label">Post As / Company <span>*</span></label>
            <select
              name="postAs"
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              className="ccj-select"
            >
              <option value="self">👤 My Name — {user?.name || 'Consultant'} (Independent)</option>
              {companies.length > 0 && (
                <optgroup label="My Associated Companies">
                  {companies.map(company => (
                    <option key={company._id} value={company._id}>
                      🏢 {company.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {selectedCompanyId === 'self'
              ? <p className="ccj-hint">✔ Posting as independent consultant under your name</p>
              : <p className="ccj-hint ccj-hint-company">✔ Posting on behalf of: <strong>{companies.find(c => c._id === selectedCompanyId)?.name}</strong></p>
            }
            {companies.length === 0 && (
              <p className="ccj-hint">No associated companies yet. You can still post under your own name, or <a href="/consultant/companies" className="ccj-link">add company associations</a>.</p>
            )}

            {selectedCompanyId !== 'self' && companies.length > 0 && (
              <div className="ccj-field">
                <label className="ccj-checkbox-label">
                  <input
                    type="checkbox"
                    checked={showCompanyName}
                    onChange={(e) => setShowCompanyName(e.target.checked)}
                  />
                  <span>Show company name alongside my name?</span>
                </label>
                <p className="ccj-hint">
                  {showCompanyName
                    ? `Job will show: "${user?.name || 'Consultant'} (on behalf of ${companies.find(c => c._id === selectedCompanyId)?.name})"`
                    : `Job will show only: "${user?.name || 'Consultant'}"`
                  }
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Basic Information */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">work</span>
            Basic Information
          </h2>
          <div className="ccj-grid">
            <div className="ccj-field">
              <label className="ccj-label">Job Title <span>*</span></label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="ccj-input"
                placeholder="Job Title"
                required
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Job Type <span>*</span></label>
              <select
                name="jobType"
                value={formData.jobType}
                onChange={handleChange}
                className="ccj-select"
                required
              >
                <option value="">Select Job Type</option>
                {jobTypes.map(type => (
                  <option key={type._id} value={type._id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Work Mode <span>*</span></label>
              <select
                name="workMode"
                value={formData.workMode}
                onChange={handleChange}
                className="ccj-select"
                required
              >
                <option value="onsite">Onsite</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Shift Type</label>
              <select
                name="shiftType"
                value={formData.shiftType}
                onChange={handleChange}
                className="ccj-select"
              >
                <option value="">Select Shift Type</option>
                {shiftTypes.map(type => (
                  <option key={type._id} value={type._id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Number of Positions</label>
              <input
                type="number"
                name="numberOfPositions"
                value={formData.numberOfPositions}
                onChange={handleChange}
                className="ccj-input"
                min="1"
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">location_on</span>
            Location
          </h2>
          <div className="ccj-grid">
            <div className="ccj-field">
              <label className="ccj-label">Address</label>
              <input
                type="text"
                name="location.address"
                value={formData.location.address}
                onChange={handleChange}
                className="ccj-input"
                placeholder="Street address"
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Country</label>
              <input
                type="text"
                name="location.country"
                value={formData.location.country || ''}
                onChange={handleChange}
                className="ccj-input"
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">State</label>
              <input
                type="text"
                name="location.state"
                value={formData.location.state || ''}
                onChange={handleChange}
                className="ccj-input"
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">City</label>
              <input
                type="text"
                name="location.city"
                value={formData.location.city}
                onChange={handleChange}
                className="ccj-input"
              />
            </div>
          </div>
        </div>

        {/* Job Description */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">description</span>
            Job Description
          </h2>
          <div className="ccj-field ccj-field-full">
            <label className="ccj-label">Description <span>*</span></label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="ccj-textarea"
              placeholder="Describe the job role..."
              rows="5"
              required
            />
          </div>
          <div className="ccj-field ccj-field-full" style={{marginTop:'10px'}}>
            <label className="ccj-label">Additional Requirements</label>
            <textarea
              name="requirements"
              value={formData.requirements}
              onChange={handleChange}
              className="ccj-textarea"
              placeholder="List requirements and qualifications..."
              rows="5"
            />
          </div>
          <div className="ccj-field ccj-field-full" style={{marginTop:'10px'}}>
            <label className="ccj-label">Benefits</label>
            <div className="ccj-skills-grid">
              {benefits.map(b => (
                <label key={b._id} className={`ccj-skill-checkbox ${formData.benefits.includes(b._id) ? 'ccj-skill-selected ccj-skill-benefit' : ''}`}>
                  <input type="checkbox" value={b._id} checked={formData.benefits.includes(b._id)} onChange={handleBenefitsChange} />
                  {b.name}
                </label>
              ))}
            </div>

            {/* Custom Benefits Section */}
            {formData.customBenefits.length > 0 && (
              <div style={{marginTop: 15}}>
                <div style={{fontSize: 14, color: '#101721', marginBottom: 8, fontWeight: 500}}>Custom Benefits:</div>
                <div className="ccj-skills-grid">
                  {formData.customBenefits.map((customBenefit, index) => (
                    <label key={`custom-${index}`} className="ccj-skill-checkbox ccj-skill-selected ccj-skill-benefit">
                      <span>{customBenefit}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomBenefit(customBenefit)}
                        style={{
                          marginLeft: 8,
                          background: 'none',
                          border: 'none',
                          color: '#f44336',
                          cursor: 'pointer',
                          fontSize: 16,
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        ×
                      </button>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="ccj-benefit-row" style={{marginTop:15}}>
              <input
                type="text"
                placeholder="Add custom benefit..."
                value={customBenefitInput}
                onChange={(e) => setCustomBenefitInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCustomBenefit()}
                className="ccj-input ccj-benefit-input"
              />
              <button type="button" onClick={handleAddCustomBenefit} className="ccj-add-btn">
                <span className="material-symbols-outlined">add</span> Add
              </button>
            </div>
          </div>
        </div>

        {/* Skills */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">psychology</span>
            Skills
          </h2>

          {consultantSkills.length > 0 && (
            <div style={{marginBottom: 15}}>
              <div style={{fontSize: 14, color: '#101721', marginBottom: 8, fontWeight: 500}}>Available Skills:</div>
              <div className="ccj-skills-grid">
                {consultantSkills.map(skill => (
                  <label
                    key={skill._id}
                    className={`ccj-skill-checkbox ${formData.requiredSkills.includes(skill._id) ? 'ccj-skill-selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      value={skill._id}
                      checked={formData.requiredSkills.includes(skill._id)}
                      onChange={handleSkillsChange}
                    />
                    <span>{skill.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveConsultantSkill(skill._id)}
                      className="ccj-skill-delete-btn"
                      title="Delete skill"
                    >
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Add Custom Skill */}
          <div className="ccj-skill-input-row" style={{marginBottom: 15}}>
            <input
              type="text"
              placeholder="Add custom skill..."
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddConsultantSkill()}
              className="ccj-skill-input"
            />
            <button type="button" onClick={handleAddConsultantSkill} className="ccj-add-skill-btn">
              <span className="material-symbols-outlined">add</span> Add Skill
            </button>
          </div>

          <div className="ccj-field ccj-field-full">
            <label className="ccj-label">Experience <span>*</span></label>
            <div className="ccj-grid">
              <div className="ccj-field">
                <label className="ccj-label">Min</label>
                <input
                  type="text"
                  name="experienceRequired.min"
                  value={formData.experienceRequired.min}
                  onChange={handleNumericChange}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="ccj-input"
                  required
                />
              </div>
              <div className="ccj-field">
                <label className="ccj-label">Max</label>
                <input
                  type="text"
                  name="experienceRequired.max"
                  value={formData.experienceRequired.max}
                  onChange={handleNumericChange}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="ccj-input"
                  required
                />
              </div>
            </div>
          </div>
          <div className="ccj-field" style={{marginTop:'10px'}}>
            <label className="ccj-label">Education Details</label>
            <select
              name="educationRequired"
              value={formData.educationRequired}
              onChange={handleChange}
              className="ccj-select"
            >
              <option value="">Select Education Level</option>
              {educations.map(edu => (
                <option key={edu._id} value={edu._id}>{edu.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Salary */}
        <div className="ccj-card">
          <h2 className="ccj-card-title">
            <span className="material-symbols-outlined">currency_rupee</span>
            Salary
          </h2>
          <div className="ccj-grid">
            <div className="ccj-field">
              <label className="ccj-label">Min Salary</label>
              <input
                type="number"
                name="salary.min"
                value={formData.salary.min}
                onChange={handleChange}
                className="ccj-input"
                min="0"
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Max Salary</label>
              <input
                type="number"
                name="salary.max"
                value={formData.salary.max}
                onChange={handleChange}
                className="ccj-input"
                min="0"
              />
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Currency</label>
              <select
                name="salary.currency"
                value={formData.salary.currency}
                onChange={handleChange}
                className="ccj-select"
              >
                <option value="INR">INR (₹)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </div>
            <div className="ccj-field">
              <label className="ccj-label">Period</label>
              <select
                name="salary.period"
                value={formData.salary.period}
                onChange={handleChange}
                className="ccj-select"
              >
                <option value="hourly">Per Hour</option>
                <option value="daily">Per Day</option>
                <option value="monthly">Per Month</option>
                <option value="yearly">Per Year</option>
              </select>
            </div>
            <div className="ccj-field ccj-field-full">
              <label className="ccj-checkbox-label">
                <input
                  type="checkbox"
                  name="salary.negotiable"
                  checked={formData.salary.negotiable}
                  onChange={handleChange}
                />
                <span>Mark salary as Negotiable</span>
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="ccj-actions">
          <button
            type="button"
            onClick={() => navigate('/consultant/jobs')}
            className="ccj-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, 'draft')}
            className="ccj-btn-submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="ccj-spinner"></span>
                Creating...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined">save</span>
                Create Job
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConsultantCreateJob;
